import {
  DEFAULT_LANDING_PAGE_CONFIG,
  type LandingPageBlock,
  parseLandingPageConfig,
} from '@provision/landing-page-utils';
import {
  type LandingPageBlockValues,
  type LandingPageFormValues,
  type LandingPageSettingsValues,
} from 'src/pages/Settings/memberships/components/landing-page-tab/types';
import { emptyBlockValues } from 'src/pages/Settings/memberships/utils/create-landing-page-block';

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function mapBlock(block: LandingPageBlock): LandingPageBlockValues {
  const values: LandingPageBlockValues = {
    id: block.id,
    type: block.type,
    ...emptyBlockValues(),
  };

  switch (block.type) {
    case 'hero':
      return {
        ...values,
        heroHeadline: block.headline,
        heroSubtext: asString(block.subtext),
        heroMediaKind: block.mediaKind,
        heroVideoUrl: asString(block.video?.mediaUrl),
        heroVideoTitle: asString(block.video?.title),
        heroVideoCaption: asString(block.video?.caption),
        heroImageUrl: asString(block.image?.url),
      };
    case 'content':
      return {
        ...values,
        contentKicker: asString(block.kicker),
        contentTitle: block.title,
        contentBody: block.body,
        contentImageUrl: asString(block.imageUrl),
        contentMediaPosition: block.mediaPosition,
      };
    case 'closingCta':
      return {
        ...values,
        ctaHeadline: block.headline,
        ctaSubtext: asString(block.subtext),
        ctaButtonLabel: block.buttonLabel,
      };
    default:
      return values;
  }
}

export function mapLandingPageConfigToBlockValues(
  rawConfig: string | null | undefined,
): LandingPageBlockValues[] {
  const config = parseLandingPageConfig(rawConfig) ?? DEFAULT_LANDING_PAGE_CONFIG;

  return config.blocks.map(mapBlock);
}

export function mapLandingPageConfigToSettingsValues(
  rawConfig: string | null | undefined,
): LandingPageSettingsValues {
  const { settings } = parseLandingPageConfig(rawConfig) ?? DEFAULT_LANDING_PAGE_CONFIG;

  return {
    ctaMode: settings.cta.mode,
    consultationUrl: asString(settings.cta.consultationUrl),
    showPricing: settings.showPricing,
    logoSize: settings.logoSize,
    primaryColor: asString(settings.colors.primary),
    secondaryColor: asString(settings.colors.secondary),
    accentColor: asString(settings.colors.accent),
  };
}

export function mapLandingPageConfigToFormValues(
  row: { name: string; slug: string; config: string } | null | undefined,
): LandingPageFormValues {
  return {
    name: row?.name ?? '',
    slug: row?.slug ?? '',
    blocks: mapLandingPageConfigToBlockValues(row?.config),
    settings: mapLandingPageConfigToSettingsValues(row?.config),
  };
}
